import { reportError } from "@/common/application/debug";
import { asCurrency } from "@/common/utilities/math/asCurrency";
import {
    getMinorUnitDigits,
    minorUnitFactor,
} from "@/common/utilities/money/currencyPrecision";
import DineroFactory, { Currency, Dinero } from "dinero.js";
import { DateTime } from "luxon";

/**
 * Exchange rates, from Frankfurter's v2 endpoint with currency-api behind it.
 *
 * Frankfurter is primary because its rates come from central banks and official
 * sources, which is the provenance you want even for figures that are only ever
 * indicative. It has to be **v2**: v1 serves the ECB reference set, roughly
 * thirty currencies with no Gulf currencies at all, so asking it for AED simply
 * returns nothing. v2 quotes 165.
 *
 * currency-api is the fallback for two reasons. One host means one outage takes
 * every price comparison on the site with it. And Frankfurter retires a currency
 * when a country adopts the euro -- BGN and HRK are both gone -- so a location
 * still priced in one of those can only be converted by a provider that keeps
 * quoting it.
 *
 * The two return different shapes, so each provider carries its own parser and
 * both normalise to upper-case ISO codes.
 */
export interface RateProvider {
    buildUrl: (base: string, isoDate: string | null) => string;
    parse: (payload: unknown) => Record<string, number>;
}

export const RATE_PROVIDERS: RateProvider[] = [
    {
        // GET /v2/rates?base=USD[&date=2025-03-06]
        // -> [{ date, base, quote: "AED", rate: 3.6725 }, ...]
        buildUrl: (base, isoDate) =>
            `https://api.frankfurter.dev/v2/rates?base=${base}` +
            (isoDate ? `&date=${isoDate}` : ""),
        parse: (payload) => {
            if (!Array.isArray(payload)) {
                throw new Error("Frankfurter returned a non-array payload.");
            }
            const rates: Record<string, number> = {};
            for (const row of payload as {
                quote?: unknown;
                rate?: unknown;
            }[]) {
                if (typeof row?.quote === "string" && typeof row.rate === "number") {
                    rates[row.quote.toUpperCase()] = row.rate;
                }
            }
            return rates;
        },
    },
    {
        // GET /v1/currencies/usd.json  ->  { date, usd: { aed: 3.6725, ... } }
        // The version segment takes "latest" or a plain ISO date.
        buildUrl: (base, isoDate) =>
            `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@${
                isoDate ?? "latest"
            }/v1/currencies/${base.toLowerCase()}.json`,
        parse: (payload) => {
            const data = payload as Record<string, unknown> | null;
            const quoted = data
                ? (Object.entries(data).find(
                      ([key, value]) =>
                          key !== "date" && value && typeof value === "object"
                  )?.[1] as Record<string, number> | undefined)
                : undefined;
            if (!quoted) {
                throw new Error("currency-api returned no rate map.");
            }
            return Object.fromEntries(
                Object.entries(quoted).map(([code, rate]) => [
                    code.toUpperCase(),
                    rate,
                ])
            );
        },
    },
];

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

interface CacheEntry {
    rates: Record<string, number>;
    timestamp: number;
}

const exchangeRateCache = new Map<string, CacheEntry>();

// Track in-flight requests so a screen full of prices triggers one fetch, not one per price.
const inflightRequests = new Map<string, Promise<Record<string, number>>>();

/**
 * Every rate quoted against `base`, keyed by upper-case currency code.
 *
 * @throws when no provider answered -- the caller has to be able to tell a
 * failed lookup from a rate of 1.
 */
const fetchRates = async (
    base: string,
    isoDate: string | null
): Promise<Record<string, number>> => {
    const cacheKey = `${isoDate ?? "latest"}:${base}`;
    const cached = exchangeRateCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.rates;
    }

    const inflight = inflightRequests.get(cacheKey);
    if (inflight) return inflight;

    const request = (async () => {
        let lastError: unknown;
        for (const provider of RATE_PROVIDERS) {
            try {
                const response = await fetch(provider.buildUrl(base, isoDate));
                if (!response.ok) {
                    throw new Error(
                        `Exchange rate lookup failed with ${response.status}.`
                    );
                }
                const rates = provider.parse(await response.json());
                if (Object.keys(rates).length === 0) {
                    throw new Error(
                        `Exchange rate response carried no rates for ${base}.`
                    );
                }
                exchangeRateCache.set(cacheKey, {
                    rates,
                    timestamp: Date.now(),
                });
                return rates;
            } catch (error) {
                lastError = error;
            }
        }
        throw lastError instanceof Error
            ? lastError
            : new Error("Exchange rate lookup failed.");
    })().finally(() => {
        inflightRequests.delete(cacheKey);
    });

    inflightRequests.set(cacheKey, request);
    return request;
};

/**
 * Units of `toCurrency` per unit of `currency`, or null when no rate could be
 * obtained. Same-currency pairs answer 1 without a network call.
 */
export const getExchangeRate = async (
    currency: string,
    toCurrency: string,
    onDate?: DateTime
): Promise<number | null> => {
    const from = currency.toUpperCase();
    const to = toCurrency.toUpperCase();
    if (from === to) return 1;

    const isoDate = onDate?.toISODate() ?? null;
    try {
        const rates = await fetchRates(from, isoDate);
        const rate = rates[to];
        if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) {
            reportError(
                `No exchange rate for ${currency} -> ${toCurrency} on ${
                    isoDate ?? "latest"
                }.`
            );
            return null;
        }
        return rate;
    } catch (error) {
        reportError(
            `Unable to fetch exchange rates for ${currency} on ${
                isoDate ?? "latest"
            }.`
        );
        reportError(error);
        return null;
    }
};

export interface CurrencyConversion {
    /**
     * The converted amount when `converted` is true, and the original amount in
     * its original currency when it is false.
     */
    amount: Dinero;
    /**
     * Whether `amount` is actually expressed in the requested currency. False
     * covers both "no conversion was needed" and "no rate was available", so a
     * caller comparing against a bound in the requested currency must check
     * this before trusting the number.
     */
    converted: boolean;
    /** Units of the target currency per unit of the source, when known. */
    rate?: number;
}

/**
 * Converts an amount in minor units from one currency to another.
 *
 * Both ends are converted through major units rather than Dinero's own
 * `convert`, because the minor unit is not the same size on both sides: 1,000
 * JPY minor units is ¥1,000 while 1,000 EUR minor units is €10, and a
 * conversion that keeps the source precision gets that wrong by a factor of
 * 100.
 *
 * @param value The amount in `currency`'s minor unit.
 * @param currency The currency the value is in.
 * @param toCurrency The currency to express it in.
 * @param onDate The date to use the rate from; omit for the latest rate.
 */
export const convertCurrency = async (
    value: number,
    currency: string,
    toCurrency: string,
    onDate?: DateTime
): Promise<CurrencyConversion> => {
    const original = asCurrency(value, currency);
    if (currency === toCurrency) {
        return { amount: original, converted: false, rate: 1 };
    }

    const rate = await getExchangeRate(currency, toCurrency, onDate);
    if (rate === null) {
        // Deliberately handing back the original currency, flagged: the caller
        // decides whether an unconvertible price is shown as-is or dropped.
        return { amount: original, converted: false };
    }

    const majorUnits = value / minorUnitFactor(currency);
    const convertedMinorUnits = Math.round(
        majorUnits * rate * minorUnitFactor(toCurrency)
    );

    return {
        amount: DineroFactory({
            amount: convertedMinorUnits,
            currency: toCurrency as Currency,
            precision: getMinorUnitDigits(toCurrency),
        }),
        converted: true,
        rate,
    };
};
